import React, { useEffect, useState } from "react";
import {
    Badge,
    Box,
    Button,
    HStack,
    Heading,
    Input,
    Select,
    SimpleGrid,
    Spinner,
    Text,
    VStack,
    useToast,
} from "@chakra-ui/react";
import FinanceSectionCard from "./common/FinanceSectionCard";
import FinanceStatCard from "./common/FinanceStatCard";
import FinanceDataTable, {
    FinanceDataTableColumn,
} from "./common/FinanceDataTable";
import { financeApi } from "./financeApi";

const money = (value: number) => `MWK ${Number(value || 0).toLocaleString()}`;

const getStatusScheme = (status?: string) => {
    if (status === "paid_out" || status === "paid") return "green";
    if (status === "eligible") return "blue";
    if (status === "reversed" || status === "failed") return "red";
    if (status === "pending") return "orange";
    return "gray";
};

const AdminDriverEarningsPage: React.FC = () => {
    const toast = useToast();

    const [loading, setLoading] = useState(true);
    const [driverId, setDriverId] = useState("");
    const [status, setStatus] = useState("");
    const [rows, setRows] = useState<any[]>([]);
    const [payouts, setPayouts] = useState<any[]>([]);
    const [summary, setSummary] = useState<any>(null);

    const load = async () => {
        try {
            setLoading(true);

            const res = await financeApi.getAdminDriverEarnings({
                driverId,
                status,
            });

            setRows(res?.data || []);
            setPayouts(res?.payouts || []);
            setSummary(res?.summary || null);
        } catch (error: any) {
            toast({
                status: "error",
                title: "Failed to load driver earnings",
                description:
                    error?.response?.data?.message ||
                    error?.message ||
                    "Please try again.",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const earningsColumns: Array<FinanceDataTableColumn<any>> = [
        {
            key: "driver",
            header: "Driver",
            render: (row) => row.driverId || "—",
        },
        {
            key: "sourceType",
            header: "Source Type",
            render: (row) => row.sourceType || "—",
        },
        {
            key: "sourceId",
            header: "Source ID",
            render: (row) => row.sourceId || "—",
        },
        {
            key: "order",
            header: "Order",
            render: (row) => row.orderId || "—",
        },
        {
            key: "amount",
            header: "Amount",
            isNumeric: true,
            render: (row) => money(row.amount),
        },
        {
            key: "status",
            header: "Status",
            render: (row) => (
                <Badge colorScheme={getStatusScheme(row.status)}>
                    {row.status || "pending"}
                </Badge>
            ),
        },
        {
            key: "batch",
            header: "Batch",
            render: (row) => row.payoutBatchId || "—",
        },
    ];

    const payoutsColumns: Array<FinanceDataTableColumn<any>> = [
        {
            key: "driver",
            header: "Driver",
            render: (row) => row.driverId || "—",
        },
        {
            key: "amount",
            header: "Amount",
            isNumeric: true,
            render: (row) => money(row.amount),
        },
        {
            key: "status",
            header: "Status",
            render: (row) => (
                <Badge colorScheme={getStatusScheme(row.status)}>
                    {row.status || "pending"}
                </Badge>
            ),
        },
        {
            key: "transferReference",
            header: "Transfer Ref",
            render: (row) => row.transferReference || "—",
        },
        {
            key: "batch",
            header: "Batch",
            render: (row) => row.payoutBatchId || "—",
        },
    ];

    return (
        <Box p={6}>
            <Heading size="lg" mb={6}>
                Admin Driver Earnings
            </Heading>

            <FinanceSectionCard title="Filters">
                <HStack spacing={3} align="end" flexWrap="wrap">
                    <Box>
                        <Text mb={1}>Driver ID</Text>
                        <Input
                            value={driverId}
                            onChange={(e) => setDriverId(e.target.value)}
                            placeholder="Driver ID"
                        />
                    </Box>

                    <Box>
                        <Text mb={1}>Status</Text>
                        <Select
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            placeholder="All statuses"
                        >
                            <option value="pending">pending</option>
                            <option value="eligible">eligible</option>
                            <option value="paid_out">paid_out</option>
                            <option value="reversed">reversed</option>
                        </Select>
                    </Box>

                    <Button onClick={load}>Apply</Button>
                </HStack>
            </FinanceSectionCard>

            {loading ? (
                <VStack py={10}>
                    <Spinner />
                    <Text>Loading driver earnings...</Text>
                </VStack>
            ) : (
                <>
                    <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4}>
                        <FinanceStatCard
                            label="Total"
                            value={money(summary?.totalAmount || 0)}
                        />
                        <FinanceStatCard
                            label="Eligible"
                            value={money(summary?.eligibleAmount || 0)}
                        />
                        <FinanceStatCard
                            label="Pending"
                            value={money(summary?.pendingAmount || 0)}
                        />
                        <FinanceStatCard
                            label="Paid Out"
                            value={money(summary?.paidOutAmount || 0)}
                        />
                    </SimpleGrid>

                    <FinanceSectionCard title="Earnings">
                        <FinanceDataTable
                            rows={rows}
                            columns={earningsColumns}
                            getRowKey={(row, index) => String(row._id || index)}
                            emptyText="No driver earnings found."
                            initialPageSize={10}
                        />
                    </FinanceSectionCard>

                    <FinanceSectionCard title="Driver Payouts">
                        <FinanceDataTable
                            rows={payouts}
                            columns={payoutsColumns}
                            getRowKey={(row, index) => String(row._id || index)}
                            emptyText="No driver payouts found."
                            initialPageSize={10}
                        />
                    </FinanceSectionCard>
                </>
            )}
        </Box>
    );
};

export default AdminDriverEarningsPage;